import addReadWriteRecipients from './addReadWriteRecipients';
import findReadWriteRecipient from './findReadWriteRecipient';
import setDirectorySearchType from './setDirectorySearchType';
import setIsSearching from './setIsSearching';
import getDisplayNameAndAddressFromRecipientString from 'owa-recipient-email-address/lib/utils/getDisplayNameAndAddressFromRecipientString';
import parseOneOffEmailAddressFromString from 'owa-recipient-email-address/lib/utils/parseOneOffEmailAddressFromString';
import dangerousCreateSMTPEmailAddressWithoutValidation from 'owa-recipient-email-address/lib/utils/dangerousCreateSMTPEmailAddressWithoutValidation';
import { DirectorySearchType } from 'owa-recipient-types/lib/types/FindControlViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import isValidRecipientAddress from 'owa-recipient-email-address/lib/utils/isValidRecipientAddress';
import { action } from 'satcheljs/lib/legacy';

export default action('resolveQueryStringToRecipient')(async function resolveQueryStringToRecipient(
    recipientWell: RecipientWellWithFindControlViewState,
    shouldDirectlyResolve: boolean,
    searchDirectory?: boolean,
    resolveIfSingle?: boolean
): Promise<void> {
    setDirectorySearchType(
        recipientWell,
        searchDirectory ? DirectorySearchType.Manual : DirectorySearchType.None
    );
    let inputText = recipientWell.queryString.trim();
    if (inputText) {
        if (shouldDirectlyResolve) {
            setIsSearching(recipientWell, false /* isSearching */);
            // try to parse out a suspected name + email address combo
            let nameAndAddress = getDisplayNameAndAddressFromRecipientString(inputText);
            let parsedAddress = parseOneOffEmailAddressFromString(
                nameAndAddress.address,
                nameAndAddress.displayName
            );
            if (parsedAddress) {
                addReadWriteRecipients(recipientWell, [
                    {
                        isValid: true,
                        newPersona: {
                            EmailAddress: parsedAddress,
                        },
                    },
                ]);
            } else {
                // When we fail to parse an address, we lie and say that it was an SMTP address with
                // the matched input string.
                //
                // TODO (#56102) why do we do this? Why isn't it conditional on '@' like when we're `inForceResolve`, as below?
                addReadWriteRecipients(recipientWell, [
                    {
                        isValid: false,
                        newPersona: {
                            EmailAddress: dangerousCreateSMTPEmailAddressWithoutValidation(
                                nameAndAddress.address,
                                nameAndAddress.displayName
                            ),
                        },
                    },
                ]);
            }
        } else {
            if (recipientWell.inForceResolve) {
                setIsSearching(recipientWell, false /* isSearching */);
                const parsedAddress = parseOneOffEmailAddressFromString(inputText);
                if (parsedAddress) {
                    // If we succeeded in parsing a recipient, it is a valid one-off recipient.
                    addReadWriteRecipients(recipientWell, [
                        {
                            isValid: true,
                            newPersona: {
                                EmailAddress: parsedAddress,
                            },
                        },
                    ]);
                } else if (inputText.indexOf('@') !== -1) {
                    // Otherwise since we're in force-resolve, we don't resolve the persona but set
                    // newEmailAddress on the recipient.
                    //
                    // TODO (#56102) who needs this? Why is it conditional on @ instead of being conditional on
                    // if the querystring is an email address? Wht is the usecase for a recipient with an
                    // @ who has an invalid SMTP?
                    addReadWriteRecipients(recipientWell, [
                        {
                            isValid: isValidRecipientAddress(inputText),
                            newPersona: null,
                            newEmailAddress: inputText,
                        },
                    ]);
                }
            } else {
                setIsSearching(recipientWell, true /* isSearching */);
                await findReadWriteRecipient(
                    recipientWell,
                    recipientWell.queryString,
                    searchDirectory,
                    resolveIfSingle
                );
            }
        }
    } else {
        // If query string is empty, hide find result with existing result cleared.
        setIsSearching(recipientWell, false /* isSearching */);
    }
});
